import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { projectTypeLabel, PROJECT_STATUS_STYLES } from "../lib/projects";
import { ProgressPosts } from "../components/projects/ProgressPosts";
import { FolderKanban, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(", ");
  if (typeof detail === "string" && detail) return detail;
  return fallback;
};

export default function ProjectPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/project-portal/${token}`);
      setData(res.data);
    } catch (err) {
      setError(errMsg(err, "Unable to load this project"));
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const uploadPostFiles = async (files) => {
    const ids = [];
    for (const f of files || []) {
      const fd = new FormData();
      fd.append("file", f);
      const res = await axios.post(`${API}/project-portal/${token}/progress-posts/attachments`, fd);
      ids.push(res.data.id);
    }
    return ids;
  };

  const addPost = async (text, files) => {
    try {
      const ids = await uploadPostFiles(files);
      const res = await axios.post(`${API}/project-portal/${token}/progress-posts`, { text, attachments: ids });
      toast.success("Progress update added");
      setData((d) => ({ ...d, progress_posts: res.data.progress_posts }));
      return true;
    } catch (err) {
      toast.error(errMsg(err, "Failed to add update"));
      return false;
    }
  };

  const editPost = async (postId, text, files, keptIds) => {
    try {
      const newIds = await uploadPostFiles(files);
      const res = await axios.put(`${API}/project-portal/${token}/progress-posts/${postId}`, { text, attachments: [...(keptIds || []), ...newIds] });
      toast.success("Progress note updated");
      setData((d) => ({ ...d, progress_posts: res.data.progress_posts }));
      return true;
    } catch (err) {
      toast.error(errMsg(err, "Failed to update note"));
      return false;
    }
  };

  const postFileUrl = (att, inline) =>
    `${API}/project-portal/${token}/post-files/${att.id}?inline=${inline ? "true" : "false"}`;

  const BrandHeader = () => (
    <header className="bg-[#1461ab]">
      <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
        <div className="bg-white rounded-xl p-1.5 shadow-sm shrink-0">
          <img src="/logo.jpg" alt="Onsite Technology Solutions" className="w-9 h-9 object-contain" data-testid="portal-brand-logo" />
        </div>
        <div>
          <p className="text-white font-semibold leading-tight" data-testid="portal-brand-name">Onsite Technology Solutions</p>
          <p className="text-blue-100 text-xs tracking-wide uppercase">Client Project Portal</p>
        </div>
      </div>
      <div className="h-1 bg-gradient-to-r from-[#0e4c88] via-[#1b75bc] to-[#4aa3e0]" />
    </header>
  );

  const BrandFooter = () => (
    <footer className="max-w-3xl mx-auto px-4 pb-8 pt-2 text-center" data-testid="portal-brand-footer">
      <p className="text-xs text-slate-400">
        © {new Date().getFullYear()} Onsite Technology Solutions. This is a secure client portal — please keep your access link private.
      </p>
    </footer>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <BrandHeader />
        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="max-w-md w-full border-t-4 border-t-[#1b75bc]">
            <CardContent className="py-12 text-center" data-testid="portal-error">
              <Lock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-700 font-medium">{error}</p>
              <p className="text-sm text-slate-500 mt-2">Please contact Onsite Technology Solutions if you believe this is a mistake.</p>
            </CardContent>
          </Card>
        </div>
        <BrandFooter />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <BrandHeader />
        <div className="flex-1 flex items-center justify-center text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <BrandHeader />
      <div className="flex-1 py-8 px-4">
        <div className="max-w-3xl mx-auto space-y-6" data-testid="portal-page">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center justify-center gap-2">
              <FolderKanban className="w-7 h-7 text-[#1b75bc]" />
              {data.name}
            </h1>
            <div className="flex items-center justify-center gap-2 mt-2">
              <Badge variant="secondary">{projectTypeLabel(data.project_type)}</Badge>
              <Badge className={`${PROJECT_STATUS_STYLES[data.status]} hover:bg-opacity-100 capitalize`} data-testid="portal-status-badge">{data.status}</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-2">Welcome, <b className="text-[#1461ab]">{data.invited_name}</b></p>
            {data.read_only && (
              <p className="text-sm text-amber-600 mt-1" data-testid="portal-readonly-notice">This project is currently suspended — view only.</p>
            )}
          </div>

          {data.description && (
            <Card className="border-t-4 border-t-[#1b75bc]">
              <CardHeader className="pb-2"><CardTitle className="text-base text-[#123f6d]">Project Description</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="portal-description">{data.description}</p></CardContent>
            </Card>
          )}

          {data.access_info !== undefined && data.access_info && (
            <Card className="border-t-4 border-t-[#1b75bc]" data-testid="portal-access-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-[#123f6d]"><KeyRound className="w-4 h-4 text-[#1b75bc]" /> Access Details</CardTitle>
              </CardHeader>
              <CardContent><p className="text-sm text-slate-700 whitespace-pre-wrap font-mono bg-slate-50 rounded p-3">{data.access_info}</p></CardContent>
            </Card>
          )}

          <Card className="border-t-4 border-t-[#1b75bc]">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-[#123f6d]">Work Progress</CardTitle>
              <CardDescription>Each update is an individual post. You can only edit your own posts — other people's posts are read-only. All posts and edits are date/time stamped.</CardDescription>
            </CardHeader>
            <CardContent>
              <ProgressPosts
                posts={data.progress_posts || []}
                viewerKey={data.viewer_key}
                readOnly={data.read_only}
                onAdd={addPost}
                onEdit={editPost}
                attachmentUrl={postFileUrl}
              />
            </CardContent>
          </Card>
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}
